import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callBackend } from '../../utils/api';
import { isNetworkFailure, NETWORK_FAILURE_MESSAGE } from '../../utils/networkFailure';
import { secondaryActionButtonSx, secondarySectionFrameSx } from '../../styles/secondaryMsiStyles';

// The query cache holds metadata only: each credential's status and the last
// four characters of its key. A key typed here stays in its password input
// until it is sent once, in the PUT body. It is never a mutation variable,
// query data or React state.
export const SYSTEM_CREDENTIALS_QUERY_KEY = ['admin', 'system-credentials'];
const ENDPOINT = 'admin/system-credentials';
// Shared with the Market calendar tab, which checks for an OpenAI key.
export const systemCredentialsQuery = {
  queryKey: SYSTEM_CREDENTIALS_QUERY_KEY,
  queryFn: ({ signal }) => callBackend(ENDPOINT, 'GET', null, { signal }),
  retry: false,
  refetchOnWindowFocus: false,
};
export const HELP_TEXT = 'Stored in Google Secret Manager for this environment. The app never shows the key '
  + 'again: replace it here and revoke the old key at OpenAI.';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STATUS_LINES = {
  empty: 'Not set',
  not_set_up: 'Not set up for this environment: an operator must run the one-time setup',
  not_configured: 'Not configured for this environment',
  secret_manager_unavailable: 'Status unavailable',
};

const pad = (value) => String(value).padStart(2, '0');

// "28 Oct 2026, 14:05", in the viewer's time zone.
export function formatUpdated(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function statusLine(credential) {
  if (credential?.status !== 'set') return STATUS_LINES[credential?.status] || 'Status unavailable';
  const updated = formatUpdated(credential.created_at);
  return ['Set', credential.last4 && `ends in ${credential.last4}`, updated && `updated ${updated}`]
    .filter(Boolean)
    .join(' · ');
}

function errorText(error) {
  if (isNetworkFailure(error)) return NETWORK_FAILURE_MESSAGE;
  return error?.message || 'The request failed. Try again.';
}

function withCredential(current, updated) {
  if (!Array.isArray(current?.credentials) || !updated?.id) return current;
  return {
    ...current,
    credentials: current.credentials.map((item) => (item.id === updated.id ? updated : item)),
  };
}

function CredentialCard({ credential }) {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [hasValue, setHasValue] = useState(false);
  const [result, setResult] = useState(null);
  const path = `${ENDPOINT}/${credential.id}`;

  const clearField = () => {
    if (inputRef.current) inputRef.current.value = '';
    setHasValue(false);
  };
  const save = useMutation({
    // The key is read from its input when the request is sent.
    mutationFn: () => callBackend(path, 'PUT', { value: inputRef.current?.value ?? '' }),
    onSuccess: (updated) => {
      queryClient.setQueryData(SYSTEM_CREDENTIALS_QUERY_KEY, (current) => withCredential(current, updated));
      setResult({
        severity: 'success',
        text: updated?.last4 ? `Saved. The stored key ends in ${updated.last4}.` : 'Saved.',
      });
    },
    onError: (error) => {
      setResult({ severity: 'error', text: `Not saved. ${errorText(error)}` });
      // A save that got no clear answer may still have stored the key.
      queryClient.invalidateQueries({ queryKey: SYSTEM_CREDENTIALS_QUERY_KEY });
    },
    onSettled: clearField,
  });
  const test = useMutation({
    mutationFn: () => callBackend(`${path}/test`, 'POST'),
    onSuccess: (outcome) => setResult(outcome?.ok
      ? { severity: 'success', text: `Test passed. ${outcome.message}` }
      : { severity: 'error', text: `Test failed. ${outcome?.message || 'OpenAI gave no answer.'}` }),
    onError: (error) => setResult({ severity: 'error', text: `Couldn't test the key. ${errorText(error)}` }),
  });
  const busy = save.isPending || test.isPending;
  const isSet = credential.status === 'set';
  const detail = ['empty', 'not_set_up'].includes(credential.status) ? null : credential.message;

  const onSubmit = (event) => {
    event.preventDefault();
    if (!hasValue || busy) return;
    setResult(null);
    save.mutate();
  };
  const onTest = () => {
    setResult(null);
    test.mutate();
  };

  return (
    <Box
      component="section"
      aria-label={credential.label}
      data-testid={`system-credential-${credential.id}`}
      sx={{ ...secondarySectionFrameSx, px: { xs: 1.4, md: 1.8 }, py: 1.4, display: 'grid', gap: 1.1 }}
    >
      <Box sx={{ display: 'grid', gap: 0.3 }}>
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{credential.label}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
          {`Used by: ${credential.used_by}`}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: isSet && credential.last4 ? 'success.main' : 'warning.main',
          }}
        >
          {statusLine(credential)}
        </Typography>
        {detail && (
          <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{detail}</Typography>
        )}
      </Box>
      <Box
        component="form"
        noValidate
        onSubmit={onSubmit}
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}
      >
        <TextField
          inputRef={inputRef}
          type="password"
          label={`New ${credential.label}`}
          size="small"
          autoComplete="off"
          disabled={busy}
          onChange={(event) => setHasValue(event.target.value.trim().length > 0)}
          inputProps={{ spellCheck: 'false', autoCapitalize: 'off', autoCorrect: 'off' }}
          helperText={HELP_TEXT}
          sx={{ flex: '1 1 320px', minWidth: 0 }}
        />
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!hasValue || busy}
          startIcon={save.isPending ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ ...secondaryActionButtonSx, mt: 0.25 }}
        >
          {save.isPending ? 'Testing and saving…' : 'Save'}
        </Button>
        {isSet && (
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={onTest}
            startIcon={test.isPending ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ ...secondaryActionButtonSx, mt: 0.25 }}
          >
            {test.isPending ? 'Testing…' : 'Test key'}
          </Button>
        )}
      </Box>
      {result && (
        <Alert severity={result.severity} onClose={() => setResult(null)} sx={{ py: 0.25 }}>
          {result.text}
        </Alert>
      )}
    </Box>
  );
}

CredentialCard.propTypes = {
  credential: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    used_by: PropTypes.string,
    status: PropTypes.string,
    message: PropTypes.string,
    created_at: PropTypes.string,
    last4: PropTypes.string,
  }).isRequired,
};

export default function SystemCredentialsPanel() {
  const query = useQuery(systemCredentialsQuery);

  if (query.isPending) {
    return (
      <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Loading credentials…</Typography>
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={() => query.refetch()}>Retry</Button>}
      >
        {`Couldn't load the credentials. ${errorText(query.error)}`}
      </Alert>
    );
  }
  const credentials = Array.isArray(query.data?.credentials) ? query.data.credentials : [];
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {credentials.map((credential) => (
        <CredentialCard key={credential.id} credential={credential} />
      ))}
    </Box>
  );
}
